import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';
import { getAuthToken } from '../utils/storage';
import { extractApiError } from '../utils/apiError';

import { applyWarehouseFilter, warehouseParamsSerializer } from '../utils/warehouseParams';

/**
 * Axios instance for HSN code API calls
 * - Shares common base URL `${API_BASE_URL}/api/v1`
 */
// Create axios instance for HSN codes API calls
const hsnAPI = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: warehouseParamsSerializer,
});

/** Attach auth token to outgoing HSN requests when available */
hsnAPI.interceptors.request.use(
  (config) => {
    const token = getAuthToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return applyWarehouseFilter(config);
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor for HSN API
 * - Clears storage and redirects to login on 401 responses
 */
hsnAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - clear storage and redirect to login
      localStorage.removeItem('auth_token');
      localStorage.removeItem('access_token');
      localStorage.removeItem('user_data');
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('user_data');

      // Redirect to login page
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

const BASE_URL = '/hsn-codes';

/**
 * HSN Service - Handles all API calls related to HSN code management
 * Exposes helper methods for CRUD and bulk operations
 */
const hsnService = {
  /**
   * Get all HSN codes with optional filters
   * @param {Object} params - Query parameters
   * @param {number} params.skip - Number of records to skip (pagination)
   * @param {number} params.limit - Number of records to return
   * @param {string} params.search - Search term
   * @param {string} params.hsn_code - Filter by HSN code
   * @param {string} params.chapter - Filter by chapter
   * @param {number} params.gst_rate - Filter by GST rate
   * @param {number} params.min_rate - Minimum rate filter
   * @param {number} params.max_rate - Maximum rate filter
   * @returns {Promise} API response with HSN codes data
   */
  /**
   * Get all HSN codes with optional filters
   * @param {Object} params - Query parameters
   @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async getAllHsnCodes(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.skip !== undefined) queryParams.append('skip', params.skip);
      if (params.limit !== undefined) queryParams.append('limit', params.limit);
      if (params.search) queryParams.append('search', params.search);
      const qs = queryParams.toString();
      const response = await hsnAPI.get(qs ? `${BASE_URL}?${qs}` : BASE_URL);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      const errorMsg = extractApiError(error);
      return {
        success: false,
        error: errorMsg,
      };
    }
  },

  /**
   * Get a specific HSN code by ID
   * @param {number|string} hsnId - HSN code ID
   * @returns {Promise} API response with HSN code data
   */
  /**
   * Get a specific HSN code by ID
   * @param {number|string} hsnId - HSN code ID
   */
  async getHsnCodeById(hsnId) {
    try {
      const response = await hsnAPI.get(`${BASE_URL}/${hsnId}`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      const errorMsg = extractApiError(error);
      return {
        success: false,
        error: errorMsg,
      };
    }
  },

  /**
   * Create a new HSN code
   * @param {Object} hsnData - HSN code data
   * @param {string} hsnData.hsn_code - HSN code
   * @param {string} hsnData.hsn_description - HSN description
   * @param {number} hsnData.tax_id - Tax ID
   * @returns {Promise} API response with created HSN code data
   */
  /**
   * Create a new HSN code
   * @param {Object} hsnData - HSN code payload
   */
  async createHsnCode(hsnData) {
    try {
      const response = await hsnAPI.post(BASE_URL, hsnData);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      // Priority: error.message, then detail, then message
      const errMsg = extractApiError(error);
      return {
        success: false,
        error: errMsg,
      };
    }
  },

  /**
   * Update an existing HSN code
   * @param {number|string} hsnId - HSN code ID
   * @param {Object} hsnData - Updated HSN code data
   * @returns {Promise} API response with updated HSN code data
   */
  /** Update an existing HSN code */
  async updateHsnCode(hsnId, hsnData) {
    try {
      const response = await hsnAPI.put(`${BASE_URL}/${hsnId}`, hsnData);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      const errorMsg = extractApiError(error);
      return {
        success: false,
        error: errorMsg,
      };
    }
  },

  /**
   * Delete an HSN code
   * @param {number|string} hsnId - HSN code ID
   * @returns {Promise} API response
   */
  /** Delete an HSN code by ID */
  async deleteHsnCode(hsnId) {
    try {
      const response = await hsnAPI.delete(`${BASE_URL}/${hsnId}`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      const errorMsg = extractApiError(error);
      return {
        success: false,
        error: errorMsg,
      };
    }
  },

  /**
   * Delete multiple HSN codes
   * @param {Array<number|string>} hsnIds - Array of HSN code IDs
   * @returns {Promise} API response
   */
  /** Delete multiple HSN codes in bulk (array of ids) */
  async deleteHsnCodes(hsnIds) {
    try {
      // API expects raw array in request body, not an object wrapper
      const response = await hsnAPI.post(`${BASE_URL}/bulk-delete`, hsnIds);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      const errorMsg = extractApiError(error);
      return {
        success: false,
        error: errorMsg,
      };
    }
  },
};

export default hsnService;